import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../../components/ui/Button';
import { themeMarbleDark } from './marbleRoulette/gameConstants';
import type { StageDef } from './marbleRoulette/data/maps';
import type { MapEntity } from './marbleRoulette/types/MapEntity.type';
import { cloneStage, defaultWheelStage, saveCustomStageToStorage } from './marbleRoulette/stageStorage';
import {
  cloneEntity,
  entitySummary,
  insertEntity,
  removeEntityAt,
  replaceEntityAt,
} from './marbleRoulette/editor/marbleMapEditorModel';

const PREVIEW_W = 720;
const PREVIEW_H = 420;
const PREVIEW_CENTER = { x: 15, y: 62 };
const PREVIEW_PX_PER_UNIT = 13;

type DrawMode = 'none' | 'polyline';

function screenToWorld(
  clientX: number,
  clientY: number,
  canvas: HTMLCanvasElement
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const px = ((clientX - rect.left) / rect.width) * canvas.width;
  const py = ((clientY - rect.top) / rect.height) * canvas.height;
  return {
    x: PREVIEW_CENTER.x + (px - PREVIEW_W / 2) / PREVIEW_PX_PER_UNIT,
    y: PREVIEW_CENTER.y + (py - PREVIEW_H / 2) / PREVIEW_PX_PER_UNIT,
  };
}

function drawEntities(
  ctx: CanvasRenderingContext2D,
  entities: MapEntity[] | undefined,
  theme: typeof themeMarbleDark,
  selectedIndex: number | null
): void {
  if (!entities) return;
  entities.forEach((ent, idx) => {
    const selected = idx === selectedIndex;
    ctx.save();
    ctx.translate(ent.position.x, ent.position.y);
    const shape = ent.shape;
    const t = theme.entity[shape.type];
    ctx.fillStyle = shape.color ?? t.fill;
    ctx.strokeStyle = selected ? '#ffeb3b' : shape.color ?? t.outline;
    ctx.lineWidth = selected ? 0.14 : 0.06;
    switch (shape.type) {
      case 'polyline':
        if (shape.points.length > 0) {
          ctx.beginPath();
          ctx.moveTo(shape.points[0]![0], shape.points[0]![1]);
          for (let i = 1; i < shape.points.length; i++) {
            ctx.lineTo(shape.points[i]![0], shape.points[i]![1]);
          }
          ctx.stroke();
        }
        break;
      case 'box': {
        const w = shape.width * 2;
        const h = shape.height * 2;
        ctx.rotate(shape.rotation);
        ctx.fillRect(-w / 2, -h / 2, w, h);
        ctx.strokeRect(-w / 2, -h / 2, w, h);
        break;
      }
      case 'circle':
        ctx.beginPath();
        ctx.arc(0, 0, shape.radius, 0, Math.PI * 2);
        ctx.stroke();
        break;
    }
    ctx.restore();
  });
}

export type MarbleMapEditorProps = {
  initialStage: StageDef;
  onSave: (stage: StageDef) => void;
  onClose: () => void;
};

const MAX_UNDO = 50;

const MarbleMapEditor: React.FC<MarbleMapEditorProps> = ({ initialStage, onSave, onClose }) => {
  const [stage, setStage] = useState<StageDef>(() => cloneStage(initialStage));
  const stageRef = useRef(stage);
  stageRef.current = stage;

  const pastRef = useRef<StageDef[]>([]);
  const [undoDepth, setUndoDepth] = useState(0);

  const mutate = useCallback((fn: (s: StageDef) => StageDef) => {
    const cur = cloneStage(stageRef.current);
    pastRef.current = [...pastRef.current.slice(-(MAX_UNDO - 1)), cloneStage(cur)];
    setUndoDepth(pastRef.current.length);
    setStage(fn(cloneStage(cur)));
  }, []);

  const undo = useCallback(() => {
    const stack = pastRef.current;
    if (stack.length === 0) return;
    const prev = stack.pop()!;
    pastRef.current = stack;
    setUndoDepth(stack.length);
    setStage(cloneStage(prev));
    setSelectedEntityIndex(i => {
      const len = prev.entities?.length ?? 0;
      if (i == null) return null;
      return i >= len ? len > 0 ? len - 1 : null : i;
    });
  }, []);

  const [drawMode, setDrawMode] = useState<DrawMode>('none');
  const [polyDraft, setPolyDraft] = useState<[number, number][]>([]);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showJson, setShowJson] = useState(false);
  const [selectedEntityIndex, setSelectedEntityIndex] = useState<number | null>(null);
  const [polylineText, setPolylineText] = useState('');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setStage(cloneStage(initialStage));
    pastRef.current = [];
    setUndoDepth(0);
    setSelectedEntityIndex(null);
  }, [initialStage]);

  const selectedEntity =
    selectedEntityIndex != null ? stage.entities?.[selectedEntityIndex] ?? null : null;

  useEffect(() => {
    if (selectedEntity?.shape.type === 'polyline') {
      setPolylineText(selectedEntity.shape.points.map(p => `${p[0]},${p[1]}`).join('\n'));
    } else {
      setPolylineText('');
    }
  }, [selectedEntityIndex, selectedEntity]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const theme = themeMarbleDark;
    ctx.fillStyle = theme.background;
    ctx.fillRect(0, 0, PREVIEW_W, PREVIEW_H);
    ctx.save();
    ctx.translate(PREVIEW_W / 2, PREVIEW_H / 2);
    ctx.scale(PREVIEW_PX_PER_UNIT, PREVIEW_PX_PER_UNIT);
    ctx.translate(-PREVIEW_CENTER.x, -PREVIEW_CENTER.y);

    ctx.strokeStyle = '#ff6f0f';
    ctx.lineWidth = 0.08;
    ctx.setLineDash([0.15, 0.12]);
    ctx.beginPath();
    ctx.moveTo(-5, stage.goalY);
    ctx.lineTo(40, stage.goalY);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.font = '0.35px sans-serif';
    ctx.fillText(`goalY=${stage.goalY}`, -4, stage.goalY - 0.6);

    drawEntities(ctx, stage.entities, theme, selectedEntityIndex);

    if (polyDraft.length > 0) {
      ctx.strokeStyle = '#4caf50';
      ctx.lineWidth = 0.1;
      ctx.beginPath();
      ctx.moveTo(polyDraft[0]![0], polyDraft[0]![1]);
      for (let i = 1; i < polyDraft.length; i++) {
        ctx.lineTo(polyDraft[i]![0], polyDraft[i]![1]);
      }
      ctx.stroke();
      polyDraft.forEach(([x, y]) => {
        ctx.fillStyle = '#81c784';
        ctx.beginPath();
        ctx.arc(x, y, 0.12, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    ctx.restore();
  }, [stage, polyDraft, selectedEntityIndex]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  const commitPolyline = () => {
    if (polyDraft.length < 2) {
      setDrawMode('none');
      setPolyDraft([]);
      return;
    }
    const entity: MapEntity = {
      position: { x: 0, y: 0 },
      type: 'static',
      shape: { type: 'polyline', rotation: 0, points: [...polyDraft] },
      props: { density: 1, angularVelocity: 0, restitution: 0 },
    };
    const idx = stage.entities?.length ?? 0;
    mutate(s => insertEntity(s, entity));
    setPolyDraft([]);
    setDrawMode('none');
    setSelectedEntityIndex(idx);
  };

  const cancelPolyline = () => {
    setPolyDraft([]);
    setDrawMode('none');
  };

  const onCanvasPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (drawMode !== 'polyline' || !canvasRef.current) return;
    const { x, y } = screenToWorld(e.clientX, e.clientY, canvasRef.current);
    setPolyDraft(prev => [...prev, [x, y]]);
  };

  const addBox = () => {
    const n = (stage.entities?.length ?? 0) % 8;
    const entity: MapEntity = {
      position: { x: 14 + n * 0.35, y: 35 + n * 0.4 },
      type: 'static',
      shape: { type: 'box', width: 0.2, height: 0.2, rotation: -0.785 },
      props: { density: 1, angularVelocity: 0, restitution: 0.3 },
    };
    const idx = stage.entities?.length ?? 0;
    mutate(s => insertEntity(s, entity));
    setSelectedEntityIndex(idx);
  };

  const addCircle = () => {
    const n = (stage.entities?.length ?? 0) % 6;
    const entity: MapEntity = {
      position: { x: 13 + n * 0.5, y: 42 + n * 0.3 },
      type: 'static',
      shape: { type: 'circle', radius: 0.35 },
      props: { density: 1, angularVelocity: 0, restitution: 0.2 },
    };
    const idx = stage.entities?.length ?? 0;
    mutate(s => insertEntity(s, entity));
    setSelectedEntityIndex(idx);
  };

  const removeLastEntity = () => {
    const oldLen = stage.entities?.length ?? 0;
    if (oldLen === 0) return;
    const newLen = oldLen - 1;
    mutate(s => {
      const ent = [...(s.entities ?? [])];
      ent.pop();
      return { ...s, entities: ent };
    });
    setSelectedEntityIndex(prev => {
      if (newLen === 0) return null;
      if (prev == null) return newLen - 1;
      return Math.min(prev, newLen - 1);
    });
  };

  const deleteSelected = () => {
    if (selectedEntityIndex == null) return;
    const idx = selectedEntityIndex;
    const oldLen = stage.entities?.length ?? 0;
    const newLen = oldLen - 1;
    mutate(s => removeEntityAt(s, idx));
    setSelectedEntityIndex(prev => {
      if (newLen === 0) return null;
      if (prev == null) return null;
      if (prev > idx) return prev - 1;
      if (prev === idx) return Math.min(idx, newLen - 1);
      return prev;
    });
  };

  const duplicateSelected = () => {
    if (selectedEntityIndex == null || !stage.entities?.[selectedEntityIndex]) return;
    const copy = cloneEntity(stage.entities[selectedEntityIndex]!);
    copy.position = { ...copy.position, x: copy.position.x + 0.4 };
    mutate(s => insertEntity(s, copy, selectedEntityIndex! + 1));
    setSelectedEntityIndex(selectedEntityIndex + 1);
  };

  const resetToWheel = () => {
    mutate(() => defaultWheelStage());
    setPolyDraft([]);
    setDrawMode('none');
    setSelectedEntityIndex(null);
    pastRef.current = [];
    setUndoDepth(0);
  };

  const handleApplyJson = () => {
    try {
      const parsed = JSON.parse(jsonText) as StageDef;
      if (typeof parsed.title !== 'string' || typeof parsed.goalY !== 'number' || typeof parsed.zoomY !== 'number') {
        throw new Error('title, goalY, zoomY 필수');
      }
      if (!Array.isArray(parsed.entities)) throw new Error('entities 배열 필요');
      pastRef.current = [];
      setUndoDepth(0);
      setStage(parsed);
      setJsonError(null);
      setSelectedEntityIndex(null);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : String(err));
    }
  };

  const openJsonEditor = () => {
    setJsonText(JSON.stringify(stage, null, 2));
    setJsonError(null);
    setShowJson(true);
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(stage, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `marble-map-${stage.title.replace(/\s+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onPickFile: React.ChangeEventHandler<HTMLInputElement> = ev => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as StageDef;
        pastRef.current = [];
        setUndoDepth(0);
        setStage(parsed);
        setJsonError(null);
        setSelectedEntityIndex(null);
      } catch (err) {
        setJsonError(err instanceof Error ? err.message : String(err));
      }
    };
    reader.readAsText(file);
    ev.target.value = '';
  };

  const handleSave = () => {
    const next = cloneStage(stage);
    saveCustomStageToStorage(next);
    onSave(next);
  };

  const applyPolylineText = () => {
    if (selectedEntityIndex == null || selectedEntity?.shape.type !== 'polyline') return;
    const lines = polylineText.split(/\n/).map(l => l.trim()).filter(Boolean);
    const points: [number, number][] = [];
    for (const line of lines) {
      const parts = line.split(/[,\s]+/).map(Number);
      if (parts.length >= 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
        points.push([parts[0]!, parts[1]!]);
      }
    }
    if (points.length < 2) {
      setJsonError('폴리라인은 최소 2개의 좌표가 필요합니다 (각 줄: x,y)');
      return;
    }
    setJsonError(null);
    const ent = cloneEntity(selectedEntity);
    if (ent.shape.type === 'polyline') {
      ent.shape.points = points;
      mutate(s => replaceEntityAt(s, selectedEntityIndex, ent));
    }
  };

  const updateSelectedEntity = (next: MapEntity) => {
    if (selectedEntityIndex == null) return;
    mutate(s => replaceEntityAt(s, selectedEntityIndex, next));
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px',
    borderRadius: '8px',
    border: '1px solid var(--Color-line-normal, #e0e0e0)',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem',
        borderRadius: '12px',
        border: '1px solid var(--Color-line-normal, #e0e0e0)',
        background: 'var(--Color-background-sub, #f8f8f8)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3
          style={{
            margin: 0,
            fontSize: '1rem',
            fontWeight: 600,
            color: 'var(--Color-text-normal, #161616)',
          }}
        >
          맵 편집기
        </h3>
        <Button variant="line" size="small" type="button" onClick={onClose}>
          닫기
        </Button>
      </div>

      <p style={{ fontSize: '0.8125rem', color: 'var(--Color-text-normal, #666)', marginBottom: '0.75rem' }}>
        맵은 <code style={{ fontSize: '0.75rem' }}>StageDef</code> JSON 한 덩어리입니다. 확장 포인트는{' '}
        <code style={{ fontSize: '0.75rem' }}>marbleRoulette/editor/marbleMapEditorModel.ts</code> 상단 주석과{' '}
        <code style={{ fontSize: '0.75rem' }}>Box2dPhysics.createEntities</code>를 참고하면 됩니다.
      </p>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ flex: '1 1 360px', minWidth: 0 }}>
          <div style={{ display: 'grid', gap: '12px', marginBottom: '1rem', maxWidth: '480px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8125rem' }}>
              맵 이름
              <input
                type="text"
                value={stage.title}
                onChange={e => mutate(s => ({ ...s, title: e.target.value }))}
                style={inputStyle}
              />
            </label>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8125rem' }}>
                goalY (결승)
                <input
                  type="number"
                  step={0.5}
                  value={stage.goalY}
                  onChange={e => mutate(s => ({ ...s, goalY: Number(e.target.value) }))}
                  style={{ ...inputStyle, width: '120px' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '0.8125rem' }}>
                zoomY (카메라)
                <input
                  type="number"
                  step={0.5}
                  value={stage.zoomY}
                  onChange={e => mutate(s => ({ ...s, zoomY: Number(e.target.value) }))}
                  style={{ ...inputStyle, width: '120px' }}
                />
              </label>
            </div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <canvas
              ref={canvasRef}
              width={PREVIEW_W}
              height={PREVIEW_H}
              onPointerDown={onCanvasPointerDown}
              style={{
                display: 'block',
                width: '100%',
                maxWidth: `${PREVIEW_W}px`,
                borderRadius: '8px',
                border: '1px solid var(--Color-line-normal, #ccc)',
                cursor: drawMode === 'polyline' ? 'crosshair' : 'default',
                touchAction: 'none',
              }}
            />
            <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '6px' }}>
              노란 강조: 목록에서 선택한 엔티티. 폴리라인 모드에서는 캔버스에 점을 찍습니다.
            </p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            <Button variant="line" size="small" type="button" onClick={undo} disabled={undoDepth === 0}>
              실행 취소
            </Button>
            {drawMode === 'none' ? (
              <Button variant="outline" size="small" type="button" onClick={() => setDrawMode('polyline')}>
                폴리라인 그리기
              </Button>
            ) : (
              <>
                <Button variant="primary" size="small" type="button" onClick={commitPolyline} disabled={polyDraft.length < 2}>
                  폴리라인 확정
                </Button>
                <Button variant="cancel" size="small" type="button" onClick={cancelPolyline}>
                  취소
                </Button>
              </>
            )}
            <Button variant="outline" size="small" type="button" onClick={addBox}>
              박스 추가
            </Button>
            <Button variant="outline" size="small" type="button" onClick={addCircle}>
              원 추가
            </Button>
            <Button variant="line" size="small" type="button" onClick={removeLastEntity} disabled={!(stage.entities?.length)}>
              마지막 삭제
            </Button>
            <Button variant="line" size="small" type="button" onClick={deleteSelected} disabled={selectedEntityIndex == null}>
              선택 삭제
            </Button>
            <Button variant="line" size="small" type="button" onClick={duplicateSelected} disabled={selectedEntityIndex == null}>
              선택 복제
            </Button>
            <Button variant="line" size="small" type="button" onClick={resetToWheel}>
              Wheel로 초기화
            </Button>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            <Button variant="secondary" size="small" type="button" onClick={openJsonEditor}>
              JSON 편집
            </Button>
            <Button variant="outline" size="small" type="button" onClick={downloadJson}>
              JSON 다운로드
            </Button>
            <Button variant="outline" size="small" type="button" onClick={() => fileInputRef.current?.click()}>
              JSON 불러오기
            </Button>
            <input ref={fileInputRef} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={onPickFile} />
          </div>

          {showJson && (
            <div style={{ marginBottom: '12px' }}>
              <textarea
                value={jsonText}
                onChange={e => setJsonText(e.target.value)}
                spellCheck={false}
                style={{
                  width: '100%',
                  minHeight: '180px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  padding: '10px',
                  borderRadius: '8px',
                  border: '1px solid var(--Color-line-normal, #e0e0e0)',
                  boxSizing: 'border-box',
                }}
              />
              {jsonError && <p style={{ color: '#c62828', fontSize: '0.8125rem', marginTop: '6px' }}>{jsonError}</p>}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <Button variant="primary" size="small" type="button" onClick={handleApplyJson}>
                  JSON 적용
                </Button>
                <Button variant="line" size="small" type="button" onClick={() => setShowJson(false)}>
                  접기
                </Button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button variant="primary" size="medium" type="button" onClick={handleSave}>
              저장 후 이 맵 사용
            </Button>
            <Button variant="secondary" size="medium" type="button" onClick={onClose}>
              저장 없이 닫기
            </Button>
          </div>
        </div>

        <div
          style={{
            flex: '0 1 280px',
            width: '100%',
            maxWidth: '320px',
            border: '1px solid var(--Color-line-normal, #e0e0e0)',
            borderRadius: '10px',
            padding: '12px',
            background: 'var(--Color-background-normal, #fff)',
            maxHeight: 'min(70vh, 560px)',
            overflow: 'auto',
          }}
        >
          <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '8px', color: '#161616' }}>오브젝트</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
            {(stage.entities ?? []).length === 0 && (
              <span style={{ fontSize: '0.75rem', color: '#888' }}>엔티티가 없습니다. 박스·원·폴리라인을 추가하세요.</span>
            )}
            {(stage.entities ?? []).map((e, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setSelectedEntityIndex(i)}
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderRadius: '8px',
                  border: selectedEntityIndex === i ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' : '1px solid #eee',
                  background: selectedEntityIndex === i ? '#fff8f3' : '#fafafa',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                }}
              >
                {entitySummary(e, i)}
              </button>
            ))}
          </div>

          {selectedEntity && (
            <>
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '8px', color: '#161616' }}>속성</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '0.75rem' }}>
                <label>
                  pos X
                  <input
                    type="number"
                    step={0.1}
                    value={selectedEntity.position.x}
                    onChange={e => {
                      const v = Number(e.target.value);
                      const ne = cloneEntity(selectedEntity);
                      ne.position.x = v;
                      updateSelectedEntity(ne);
                    }}
                    style={{ ...inputStyle, marginTop: '4px' }}
                  />
                </label>
                <label>
                  pos Y
                  <input
                    type="number"
                    step={0.1}
                    value={selectedEntity.position.y}
                    onChange={e => {
                      const v = Number(e.target.value);
                      const ne = cloneEntity(selectedEntity);
                      ne.position.y = v;
                      updateSelectedEntity(ne);
                    }}
                    style={{ ...inputStyle, marginTop: '4px' }}
                  />
                </label>
                <label>
                  타입
                  <select
                    value={selectedEntity.type}
                    onChange={e => {
                      const ne = cloneEntity(selectedEntity);
                      ne.type = e.target.value as 'static' | 'kinematic';
                      updateSelectedEntity(ne);
                    }}
                    style={{ ...inputStyle, marginTop: '4px' }}
                  >
                    <option value="static">static</option>
                    <option value="kinematic">kinematic</option>
                  </select>
                </label>
                <label>
                  restitution (튕김)
                  <input
                    type="number"
                    step={0.05}
                    value={selectedEntity.props.restitution}
                    onChange={e => {
                      const ne = cloneEntity(selectedEntity);
                      ne.props.restitution = Number(e.target.value);
                      updateSelectedEntity(ne);
                    }}
                    style={{ ...inputStyle, marginTop: '4px' }}
                  />
                </label>
                <label>
                  density
                  <input
                    type="number"
                    step={0.1}
                    value={selectedEntity.props.density}
                    onChange={e => {
                      const ne = cloneEntity(selectedEntity);
                      ne.props.density = Number(e.target.value);
                      updateSelectedEntity(ne);
                    }}
                    style={{ ...inputStyle, marginTop: '4px' }}
                  />
                </label>
                <label>
                  angularVelocity (rad/s)
                  <input
                    type="number"
                    step={0.1}
                    value={selectedEntity.props.angularVelocity}
                    onChange={e => {
                      const ne = cloneEntity(selectedEntity);
                      ne.props.angularVelocity = Number(e.target.value);
                      updateSelectedEntity(ne);
                    }}
                    style={{ ...inputStyle, marginTop: '4px' }}
                  />
                </label>

                {selectedEntity.shape.type === 'box' && (
                  <>
                    <label>
                      width (반쪽 길이, Box2D SetAsBox)
                      <input
                        type="number"
                        step={0.05}
                        value={selectedEntity.shape.width}
                        onChange={e => {
                          const ne = cloneEntity(selectedEntity);
                          if (ne.shape.type === 'box') ne.shape.width = Number(e.target.value);
                          updateSelectedEntity(ne);
                        }}
                        style={{ ...inputStyle, marginTop: '4px' }}
                      />
                    </label>
                    <label>
                      height
                      <input
                        type="number"
                        step={0.05}
                        value={selectedEntity.shape.height}
                        onChange={e => {
                          const ne = cloneEntity(selectedEntity);
                          if (ne.shape.type === 'box') ne.shape.height = Number(e.target.value);
                          updateSelectedEntity(ne);
                        }}
                        style={{ ...inputStyle, marginTop: '4px' }}
                      />
                    </label>
                    <label>
                      회전 (도)
                      <input
                        type="number"
                        step={1}
                        value={Math.round((selectedEntity.shape.rotation * 180) / Math.PI)}
                        onChange={e => {
                          const ne = cloneEntity(selectedEntity);
                          if (ne.shape.type === 'box') {
                            ne.shape.rotation = (Number(e.target.value) * Math.PI) / 180;
                          }
                          updateSelectedEntity(ne);
                        }}
                        style={{ ...inputStyle, marginTop: '4px' }}
                      />
                    </label>
                  </>
                )}

                {selectedEntity.shape.type === 'circle' && (
                  <label>
                    반지름
                    <input
                      type="number"
                      step={0.05}
                      value={selectedEntity.shape.radius}
                      onChange={e => {
                        const ne = cloneEntity(selectedEntity);
                        if (ne.shape.type === 'circle') ne.shape.radius = Number(e.target.value);
                        updateSelectedEntity(ne);
                      }}
                      style={{ ...inputStyle, marginTop: '4px' }}
                    />
                  </label>
                )}

                {selectedEntity.shape.type === 'polyline' && (
                  <>
                    <label>
                      점 목록 (줄마다 <code>x,y</code>)
                      <textarea
                        value={polylineText}
                        onChange={e => setPolylineText(e.target.value)}
                        spellCheck={false}
                        style={{
                          ...inputStyle,
                          minHeight: '120px',
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          marginTop: '4px',
                        }}
                      />
                    </label>
                    <Button variant="primary" size="small" type="button" onClick={applyPolylineText}>
                      폴리라인 점 적용
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ fontSize: '0.8125rem', color: '#666', marginTop: '12px' }}>
        엔티티 {stage.entities?.length ?? 0}개 · 실행취소 {undoDepth}단계
        {drawMode === 'polyline' && ` · 폴리라인 초안 ${polyDraft.length}점`}
      </div>
    </div>
  );
};

export default MarbleMapEditor;
